import React, { useState, useEffect, useCallback } from 'react';
import CourseExperienceSurvey from './CourseExperienceSurvey';
import UserOnboardingSurvey from './UserOnboardingSurvey';
import custom_api_func from './axios_api_func';

const STAGE = {
  LOADING: 'loading',
  COURSE_SURVEY: 'course_survey',
  ONBOARDING_SURVEY: 'onboarding_survey',
  DONE: 'done',
};

const CourseCompletionSurveys = ({ academyid, csrftoken, course, onClose }) => {
  const [stage, setStage] = useState(STAGE.LOADING);

  // Checks onboarding survey status — only called explicitly, never on every render
  const checkOnboarding = useCallback(async () => {
    try {
      const res = await custom_api_func.get(`api/check_onboarding_survey2/${academyid}/`);
      const completed = !!res?.data?.completed;
      setStage(completed ? STAGE.DONE : STAGE.ONBOARDING_SURVEY);
    } catch (err) {
      console.error('Failed to check onboarding survey status', err);
      setStage(STAGE.DONE); // fail safe: don't block the user
    }
  }, [academyid]);

  // Checks course survey status once on mount (or if academy/course changes)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await custom_api_func.get(
          `api/check_course_survey2/${academyid}/${course.id}/`
        );
        if (cancelled) return;
        const completed = !!res?.data?.completed;
        if (!completed) {
          setStage(STAGE.COURSE_SURVEY);
        } else {
          await checkOnboarding();
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to check course survey status', err);
          setStage(STAGE.DONE);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [academyid, course.id, checkOnboarding]);

  // Once we land on DONE, tell the parent so it can stop rendering us
  useEffect(() => {
    if (stage === STAGE.DONE) {
      onClose && onClose();
    }
  }, [stage, onClose]);

  if (stage === STAGE.COURSE_SURVEY) {
    return (
      <CourseExperienceSurvey
        academyid={academyid}
        csrftoken={csrftoken}
        course={course}
        onClose={() => { checkOnboarding(); }} // move to next stage, check API once
      />
    );
  }

  if (stage === STAGE.ONBOARDING_SURVEY) {
    return (
      <UserOnboardingSurvey
        academyid={academyid}
        csrftoken={csrftoken}
        onClose={() => setStage(STAGE.DONE)}
      />
    );
  }

  return null; // loading or done — render nothing
};

export default CourseCompletionSurveys;